// TES-1290 (TES-1267 §2 B and §4; A-02/09C "SLUTLIGT KANONISKT BESLUT
// 2026-10-12") — "Läkarens kommentar till behandlingsbeslutet": the ONE
// patient free text that goes with a treatment decision, whatever its kind.
//
//   "Ingen separat fritext ska skapas för medicinsk månadsåterkoppling,
//    receptförnyelse, dosändring eller ändrad behandlingsplan. Dessa använder B."
//   "Kommentaren är automatiskt förifylld … Den får inte manuellt duplicera
//    läkemedel, styrka, dos, intervall, receptstatus eller nästa dosdatum."
//   TES-1311 (Emelie): "Läkarens kommentar till behandlingsbeslutet hör till
//    behandlingen och beskriver kort det patientspecifika valet, relevanta
//    råd och uppföljningen."
//
// This file is the one place for its label, its prefill per outcome, the
// text as published, and the duplication lint. The lint only warns: a
// doctor may have a reason to name the medication, and a refusal would
// stop a decision over wording. The structured values themselves are shown
// to the patient from their columns (Din behandling), never from this text.

// Her label, verbatim.
export const TREATMENT_COMMENT_LABEL =
  "Läkarens kommentar till behandlingsbeslutet";

// Every decision kind that carries the comment.
//   approve           — the first decision (workspace, one-step meeting)
//   kept / changed    — the first decision after kompletteringar, with the
//                       planned treatment kept or changed
//   transfer_approved — an approved prescription transfer
//   renewed / dose_changed — the monthly review
//   pause / resume / end / switch — a running treatment's decision
export const TREATMENT_COMMENT_OUTCOMES = [
  "approve",
  "kept",
  "changed",
  "transfer_approved",
  "renewed",
  "dose_changed",
  "pause",
  "resume",
  "end",
  "switch",
] as const;
export type TreatmentCommentOutcome =
  (typeof TREATMENT_COMMENT_OUTCOMES)[number];

// Proposed copy (no approved wording exists), editable before every publish
// and listed in the PR for Emelie. Each line states the assessment or the
// follow-up only. None names a medication, a strength, an interval, a
// prescription status or a date: those come from the structured data.
const ASSESSMENT: Record<TreatmentCommentOutcome, string> = {
  approve:
    "Läkaren bedömer att behandlingen är medicinskt lämplig för dig utifrån underlaget och vårdmötet.",
  kept: "Läkaren har bedömt kompletteringarna. Den planerade behandlingen kvarstår.",
  changed:
    "Läkaren har bedömt kompletteringarna och ändrat den planerade behandlingen.",
  transfer_approved:
    "Läkaren har granskat underlaget från din tidigare behandling och bedömer att den kan fortsätta hos Ella.",
  renewed:
    "Läkaren har gått igenom din månadsincheckning och bedömer att behandlingen kan fortsätta som tidigare.",
  dose_changed:
    "Läkaren har gått igenom din månadsincheckning och justerar dosen.",
  pause: "Läkaren har beslutat om ett uppehåll i din behandling.",
  resume: "Läkaren har beslutat att din behandling återupptas.",
  end: "Läkaren har beslutat att avsluta din behandling.",
  switch: "Läkaren har beslutat att byta läkemedel i din behandling.",
};

// Where the changed values are: the comment points there instead of
// repeating them.
const SEE_DIN_BEHANDLING =
  "Aktuellt läkemedel och dos ser du under Din behandling.";
const POINTS_TO_STRUCTURED: ReadonlySet<TreatmentCommentOutcome> = new Set([
  "changed",
  "dose_changed",
  "resume",
  "switch",
]);

// Ö-547's journal line, in the patient's words.
const MONTHLY_FOLLOW_UP =
  "Behandlingen följs upp varje månad via din månadsincheckning.";
const FOLLOW_UP: Record<TreatmentCommentOutcome, string | null> = {
  approve: MONTHLY_FOLLOW_UP,
  kept: MONTHLY_FOLLOW_UP,
  changed: MONTHLY_FOLLOW_UP,
  transfer_approved: MONTHLY_FOLLOW_UP,
  renewed: MONTHLY_FOLLOW_UP,
  dose_changed: MONTHLY_FOLLOW_UP,
  resume: MONTHLY_FOLLOW_UP,
  switch: MONTHLY_FOLLOW_UP,
  pause: "Läkaren återkommer till dig om när behandlingen kan återupptas.",
  end: null,
};

/** The prefilled comment for a decision kind: assessment, then follow-up. */
export function treatmentCommentPrefill(
  outcome: TreatmentCommentOutcome | null,
): string {
  if (outcome === null) return "";
  const first = [ASSESSMENT[outcome]];
  if (POINTS_TO_STRUCTURED.has(outcome)) first.push(SEE_DIN_BEHANDLING);
  const follow = FOLLOW_UP[outcome];
  return [first.join(" "), ...(follow ? [follow] : [])].join("\n\n");
}

/**
 * The comment as it is published: line endings normalised, trailing spaces
 * dropped, runs of blank lines collapsed to one, and the whole trimmed. An
 * empty result means there is no comment.
 */
export function finalizeTreatmentComment(body: string): string {
  return body
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ---- The duplication lint ---------------------------------------------------

export const TREATMENT_COMMENT_DUPLICATES = [
  "medication",
  "strength",
  "interval",
  "prescription",
  "nextDose",
] as const;
export type TreatmentCommentDuplicate =
  (typeof TREATMENT_COMMENT_DUPLICATES)[number];

/** The structured values the comment may not restate, as far as known. */
export interface TreatmentCommentStructured {
  medication?: string | null;
  dose?: string | null;
  frequency?: string | null;
}

// Medication names the doctor may write even when the structured order is
// not filled in yet (the catalogue's products and their substances).
const KNOWN_MEDICATIONS =
  /\b(mounjaro|wegovy|ozempic|saxenda|zepbound|semaglutid|tirzepatid|liraglutid)\b/i;
const STRENGTH = /\b\d+(?:[.,]\d+)?\s?(?:mg|mikrogram|µg|mcg|ml|enheter|ie)\b/i;
// Dosing intervals only: "varje månad" is the follow-up, not a dose.
const INTERVAL =
  /(\ben gång (?:i|per) (?:veckan|vecka|dygnet|dagen)\b|\bvarje (?:vecka|dag)\b|\bvarannan vecka\b|\bveckovis\b|\bdagligen\b|\/\s?(?:vecka|v|dag|dygn)\b)/i;
const PRESCRIPTION =
  /(\breceptstatus\b|\b(?:e-)?recept(?:et)?\b[^.!?\n]*\b(?:utfärdat|utfärdats|förnyat|förnyats|skickat|skickats|gäller|giltigt|hämta ut|uthämtat)\b)/i;
const MONTHS =
  "januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|okt|nov|dec";
const NEXT_DOSE = new RegExp(
  `(\\bnästa dos\\b|\\b\\d{4}-\\d{2}-\\d{2}\\b|\\b\\d{1,2}\\s(?:${MONTHS})\\b|\\b\\d{1,2}/\\d{1,2}\\b)`,
  "i",
);

function mentions(body: string, value: string | null | undefined): boolean {
  const v = value?.trim().toLowerCase();
  // Too short to say anything ("1", "v").
  if (!v || v.length < 3) return false;
  return body.toLowerCase().includes(v);
}

/**
 * What the comment restates of the structured decision, if anything. The
 * structured values match as written; the patterns catch the same facts in
 * the doctor's own words. A warning, never a refusal (see above).
 */
export function treatmentCommentDuplicates(
  body: string,
  structured: TreatmentCommentStructured = {},
): TreatmentCommentDuplicate[] {
  const out: TreatmentCommentDuplicate[] = [];
  const medicationWord = structured.medication?.trim().split(/\s+/)[0];
  if (
    KNOWN_MEDICATIONS.test(body) ||
    mentions(
      body,
      medicationWord && medicationWord.length >= 4 ? medicationWord : null,
    )
  )
    out.push("medication");
  if (STRENGTH.test(body) || mentions(body, structured.dose))
    out.push("strength");
  if (INTERVAL.test(body) || mentions(body, structured.frequency))
    out.push("interval");
  if (PRESCRIPTION.test(body)) out.push("prescription");
  if (NEXT_DOSE.test(body)) out.push("nextDose");
  return out;
}

// What the doctor reads beside the field.
export const TREATMENT_COMMENT_DUPLICATE_WARNING: Record<
  TreatmentCommentDuplicate,
  string
> = {
  medication:
    "Kommentaren nämner läkemedlet. Patienten ser läkemedlet under Din behandling från ordinationen.",
  strength:
    "Kommentaren nämner styrka eller dos. Patienten ser dosen under Din behandling från ordinationen.",
  interval:
    "Kommentaren nämner doseringsintervallet. Patienten ser intervallet under Din behandling från ordinationen.",
  prescription:
    "Kommentaren nämner receptstatus. Patienten ser receptstatusen under Din behandling när den är bekräftad.",
  nextDose:
    "Kommentaren nämner nästa dos eller ett datum. Patienten ser nästa planerade dos under Din behandling.",
};

/**
 * TES-1311 — "De ska inte duplicera varandra": the sentences the comment
 * shares with another patient text (the meeting summary, TES-1289).
 * Compared case- and whitespace-insensitively; short fragments ("Tack.")
 * are ignored.
 */
export function treatmentCommentRepeats(
  comment: string,
  other: string,
): string[] {
  const sentences = (text: string) =>
    text
      .split(/(?<=[.!?])\s+|\n+/)
      .map((s) => s.trim())
      .filter((s) => s.length >= 20);
  const key = (s: string) => s.toLowerCase().replace(/\s+/g, " ");
  const theirs = new Set(sentences(other).map(key));
  return sentences(comment).filter((s) => theirs.has(key(s)));
}
